import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ClipboardList,
  ExternalLink,
  FileText,
  Monitor,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Spinner,
  cn,
  useToast,
} from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { useSprints } from '../../features/sprintboard/useSprints';
import { usePrePlanning } from '../../features/preplanning/usePrePlanning';
import { useTaskTemplates } from '../../features/taskTemplates/useTaskTemplates';
import {
  draftFromPrePlan,
  formatMonth,
} from '../../features/preplanning/prePlanModel';
import type {
  PrePlanDraft,
  PrePlanSummary,
} from '../../features/preplanning/types';
import { PrePlanEditorModal } from './PrePlanEditorModal';

/** One selectable pre-plan entry in the month-grouped left rail. */
function EntryButton({
  entry,
  active,
  onSelect,
}: {
  entry: PrePlanSummary;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
      className={cn(
        'w-full rounded-xl border px-3 py-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active
          ? 'border-accent bg-surface-3'
          : 'border-surface-border bg-surface-1 hover:border-zinc-600',
      )}
    >
      <span className="flex items-start gap-2">
        <FileText
          className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-zinc-100">
            {entry.title}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            {entry.sprintName && (
              <Badge tone="accent">{entry.sprintName}</Badge>
            )}
            {entry.externalLink && (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                External doc
              </span>
            )}
          </span>
        </span>
      </span>
    </button>
  );
}

/** The read view of the selected pre-plan: metadata, external link, embed, markdown. */
function PrePlanDetail({
  title,
  monthLabel,
  sprintName,
  content,
  externalLink,
  loading,
  onEdit,
  onDelete,
}: {
  title: string;
  monthLabel: string;
  sprintName?: string;
  content: string;
  externalLink?: string;
  loading: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [embed, setEmbed] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading pre-plan" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-surface-border pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-zinc-100">{title}</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone="info">{monthLabel}</Badge>
            {sprintName && <Badge tone="accent">{sprintName}</Badge>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Pencil className="h-3.5 w-3.5" />}
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </div>
      </div>

      {externalLink && (
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={externalLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-medium text-white transition-colors hover:bg-accent-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open in Chorus ↗
          </a>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Monitor className="h-3.5 w-3.5" />}
            aria-pressed={embed}
            onClick={() => {
              setEmbedFailed(false);
              setEmbed((v) => !v);
            }}
          >
            {embed ? 'Hide embed' : 'Try embed'}
          </Button>
        </div>
      )}

      {externalLink && embed && (
        <div className="space-y-2 rounded-2xl border border-surface-border bg-surface-1 p-2">
          <p className="px-1 text-xs text-zinc-500">
            Embedded preview — internal docs often block embedding. If it stays
            blank,{' '}
            <a
              href={externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-fg underline underline-offset-2 hover:no-underline"
            >
              open it in a new tab
            </a>
            .
          </p>
          {embedFailed ? (
            <p className="flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-6 text-sm text-zinc-400">
              <AlertTriangle className="h-4 w-4 text-amber-300" aria-hidden="true" />
              This document couldn&apos;t be embedded. Use the link above to open
              it directly.
            </p>
          ) : (
            <iframe
              src={externalLink}
              title={`Embedded document: ${title}`}
              onError={() => setEmbedFailed(true)}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              className="h-[60vh] w-full rounded-xl border border-surface-border bg-white"
            />
          )}
        </div>
      )}

      <MarkdownView markdown={content} />
    </div>
  );
}

/**
 * PrePlanningSection — a month-wise area where the team keeps sprint-wise
 * pre-plan documents as Markdown. Entries are grouped by month (newest first) in
 * a left rail; selecting one renders its Markdown inline with a prominent
 * "Open in Chorus ↗" link and an optional embed toggle. Create / edit / delete
 * run through the {@link PrePlanEditorModal} and {@link ConfirmDialog}.
 *
 * Tenant-scoped automatically (the store routes by the active team bundle), so
 * this works with or without an active room; the optional sprint selector is
 * populated from the active room's sprints when one is selected.
 */
export function PrePlanningSection() {
  const { toast } = useToast();
  const { sprints } = useSprints();
  const { templates, loadContent } = useTaskTemplates();
  const {
    groups,
    selectedId,
    selected,
    selecting,
    loading,
    error,
    select,
    create,
    update,
    remove,
  } = usePrePlanning();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PrePlanDraft | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const total = useMemo(
    () => groups.reduce((n, g) => n + g.items.length, 0),
    [groups],
  );

  const openCreate = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    setEditing(draftFromPrePlan(selected));
    setEditorOpen(true);
  };

  const onSave = async (draft: PrePlanDraft) => {
    setSaving(true);
    try {
      if (editing && selectedId) {
        await update(selectedId, draft);
        toast({ tone: 'success', title: 'Pre-plan updated' });
      } else {
        await create(draft);
        toast({ tone: 'success', title: 'Pre-plan created' });
      }
      setEditorOpen(false);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!selectedId) return;
    setDeleting(true);
    try {
      await remove(selectedId);
      setConfirmOpen(false);
      toast({ tone: 'success', title: 'Pre-plan deleted' });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading pre-plans" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Couldn't load pre-plans"
        description={error}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-zinc-500">
          {total === 0
            ? 'Sprint-wise pre-plan docs, grouped by month.'
            : `${total} pre-plan${total === 1 ? '' : 's'} across ${groups.length} month${groups.length === 1 ? '' : 's'}.`}
        </p>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={openCreate}
        >
          New pre-plan
        </Button>
      </div>

      {total === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title="No pre-plans yet"
          description="Create the first month-wise pre-plan doc. Write Markdown inline and link the canonical planning doc."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
              New pre-plan
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr]">
          <nav
            aria-label="Pre-plans by month"
            className="space-y-4 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1"
          >
            {groups.map((group) => (
              <section key={group.month || 'undated'} aria-label={group.label}>
                <h4 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {group.label}
                </h4>
                <ul className="space-y-2">
                  {group.items.map((entry) => (
                    <li key={entry.id}>
                      <EntryButton
                        entry={entry}
                        active={entry.id === selectedId}
                        onSelect={() => void select(entry.id)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </nav>

          <div className="rounded-2xl border border-surface-border bg-surface-2/50 p-4">
            {selected ? (
              <PrePlanDetail
                title={selected.title}
                monthLabel={formatMonth(selected.month)}
                sprintName={selected.sprintName}
                content={selected.content}
                externalLink={selected.externalLink}
                loading={selecting}
                onEdit={openEdit}
                onDelete={() => setConfirmOpen(true)}
              />
            ) : selecting ? (
              <div className="flex items-center justify-center py-24">
                <Spinner label="Loading pre-plan" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-3 text-accent">
                  <FileText className="h-6 w-6" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-zinc-300">
                  Select a pre-plan
                </p>
                <p className="mt-1 max-w-xs text-xs text-zinc-500">
                  Pick a month-wise pre-plan on the left to render its Markdown
                  and open its linked doc.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <PrePlanEditorModal
        open={editorOpen}
        initial={editing}
        sprints={sprints}
        templates={templates}
        loadTemplateContent={loadContent}
        saving={saving}
        onClose={() => !saving && setEditorOpen(false)}
        onSave={(draft) => void onSave(draft)}
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Delete pre-plan?"
        message="This pre-plan will be removed from the month-wise list. This can't be undone."
        loading={deleting}
        onConfirm={() => void onConfirmDelete()}
        onClose={() => !deleting && setConfirmOpen(false)}
      />
    </div>
  );
}

export default PrePlanningSection;
